import React, {useState} from 'react';
import {
  Pressable,
  Switch,
  Text,
  TextInput,
  TouchableOpacity,
  View
} from 'react-native';
import i18n from 'i18n-js';
import {fontFamily} from '../../../helpers/fontFamily';
import RuleEngineDropdown from '../../rulesEngine/RuleEngineDropdown';
import {
  groupDimmedOpacity,
  infoIconColor,
  switchThumbColor,
  switchTrackColor,
  textColor,
  textInputBg,
  textInputHeight,
  textInputRadius,
  textInputText,
  textMutedColor,
  toggleOffColor
} from '../../../style/adminTools';
import ActionButton from './ActionButton';
import ColumnsMenu from './ColumnsMenu';
import Pill from './Pill';
import {STATUS_OPTIONS} from './phoneSortOptions';
import {TooltipAnchor, TooltipBubble} from './TooltipTarget';

const t = (key, options) => i18n.t(`adminTenantGrid.${key}`, options);

const InfoTip = ({text}) => {
  const [open, setOpen] = useState(false);
  return (
    <TooltipAnchor>
      <Pressable
        testID="allTenantSortingInfo"
        accessibilityRole="button"
        accessibilityLabel={t('allTenantSorting')}
        onPress={() => setOpen(value => !value)}
        onHoverIn={() => setOpen(true)}
        onHoverOut={() => setOpen(false)}
        onBlur={() => setOpen(false)}
        style={{marginHorizontal: 8}}
      >
        <Text style={{color: infoIconColor, fontSize: 16}}>{'ⓘ'}</Text>
      </Pressable>
      {open ? <TooltipBubble testID="allTenantSortingTip" text={text} /> : null}
    </TooltipAnchor>
  );
};

const GridToolbar = ({
  searchValue,
  onSearchChange,
  onClearSearch,
  status,
  onStatusChange,
  allSorting,
  onAllSortingChange,
  allCount,
  loading,
  selectedCount,
  actions,
  columnLabels,
  hiddenColumns,
  onToggleColumn,
  columnsReady = true
}) => {
  const [columnsOpen, setColumnsOpen] = useState(false);
  const statusOptions = STATUS_OPTIONS.map(option => ({
    value: option.value,
    label: t(option.label)
  }));
  const columnsDisabled = loading || !columnsReady;
  const shown = columnLabels.filter(
    column => !hiddenColumns[column.name]
  ).length;

  return (
    <View
      style={{
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        marginBottom: 12,
        zIndex: 10
      }}
    >
      <View
        pointerEvents={loading ? 'none' : 'auto'}
        style={{
          flexDirection: 'row',
          flexWrap: 'wrap',
          alignItems: 'center',
          opacity: loading ? groupDimmedOpacity : 1
        }}
      >
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            width: 230,
            height: 44,
            marginRight: 12,
            paddingHorizontal: 12,
            backgroundColor: textInputBg,
            borderRadius: textInputRadius
          }}
        >
          <TextInput
            testID="tenantSearchInput"
            value={searchValue}
            onChangeText={onSearchChange}
            placeholder={t('searchPlaceholder')}
            placeholderTextColor={textMutedColor}
            style={{
              flex: 1,
              height: textInputHeight,
              outlineStyle: 'none',
              ...textInputText
            }}
          />
          {searchValue ? (
            <TouchableOpacity
              testID="clearSearchButton"
              accessibilityLabel={t('clearSearch')}
              onPress={onClearSearch}
            >
              <Text style={{color: textMutedColor, fontSize: 18}}>{'×'}</Text>
            </TouchableOpacity>
          ) : null}
        </View>
        <View style={{width: 130, marginRight: 12}}>
          <RuleEngineDropdown
            testID="tenantStatusSelect"
            accessibilityLabel={t('filterStatus')}
            options={statusOptions}
            selectedValue={status}
            onSelect={onStatusChange}
          />
        </View>
        <View style={{flexDirection: 'row', alignItems: 'center'}}>
          <Text style={{...textInputText, color: textColor}}>
            {t('allTenantSorting')}
          </Text>
          <InfoTip text={t('allTenantSortingTooltip', {count: allCount})} />
          <Switch
            testID="allTenantSortingSwitch"
            accessibilityLabel={t('allTenantSorting')}
            value={allSorting}
            onValueChange={onAllSortingChange}
            trackColor={switchTrackColor}
            thumbColor={switchThumbColor}
          />
        </View>
      </View>

      <View style={{flex: 1}} />

      <View style={{flexDirection: 'row', alignItems: 'center', zIndex: 20}}>
        {loading ? (
          <Text
            testID="tenantToolbarLoading"
            style={{...textInputText, color: textMutedColor, marginRight: 12}}
          >
            {t('loading')}
          </Text>
        ) : (
          <>
            {selectedCount > 0 ? (
              <View style={{marginRight: 10}}>
                <Pill
                  testID="tenantSelectedPill"
                  text={t('selectedCount', {count: selectedCount})}
                />
              </View>
            ) : null}
            {['duplicate', 'delete', 'restore', 'deleteSummary'].map(name => (
              <View key={name} style={{marginRight: 8}}>
                <ActionButton
                  testID={`tenantAction-${name}`}
                  label={t(name)}
                  danger={name === 'delete' || name === 'deleteSummary'}
                  enabled={actions[name].enabled}
                  reason={actions[name].reason}
                  onPress={actions[name].onPress}
                />
              </View>
            ))}
          </>
        )}
        <TooltipAnchor>
          <Pressable
            testID="tenantColumnsButton"
            accessibilityRole="button"
            disabled={columnsDisabled}
            accessibilityState={{disabled: columnsDisabled}}
            onPress={() => setColumnsOpen(open => !open)}
            style={{
              height: 36,
              flexDirection: 'row',
              alignItems: 'center',
              paddingHorizontal: 14,
              borderRadius: 6,
              borderWidth: 2,
              borderColor: toggleOffColor,
              opacity: columnsDisabled ? groupDimmedOpacity : 1
            }}
          >
            <Text
              style={{
                fontFamily: fontFamily.font600,
                fontSize: 13,
                color: textColor,
                marginRight: 8
              }}
            >
              {t('columns')}
            </Text>
            <Pill
              text={t('columnsShown', {shown, total: columnLabels.length})}
            />
          </Pressable>
          {columnsOpen && !columnsDisabled ? (
            <ColumnsMenu
              columns={columnLabels}
              hidden={hiddenColumns}
              onToggle={onToggleColumn}
              onClose={() => setColumnsOpen(false)}
            />
          ) : null}
        </TooltipAnchor>
      </View>
    </View>
  );
};

export default GridToolbar;
